// hooks/useAuditLogQuery.ts
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserStore } from "@/store/userStore";

/** Ligne brute de la vue `audit_log_enriched`. */
export type AuditLogRow = Record<string, unknown>;

export interface AuditLogFilters {
  search?: string;
  userIds?: string[];
  fullNames?: string[];
  /** Jour de début inclus, au format local `yyyy-MM-dd`. */
  startDate?: string;
  /** Jour de fin inclus, au format local `yyyy-MM-dd`. */
  endDate?: string;
}

/** Plafond de sécurité pour l'export : au-delà, l'export est signalé tronqué. */
export const AUDIT_LOG_EXPORT_LIMIT = 5000;

/**
 * `created_at` est un `timestamptz`. Le comparer à une date nue (« 2026-07-25 »)
 * revenait à la lire comme 2026-07-25T00:00:00Z : la journée de fin entière
 * était exclue du filtre, et le jour de début décalé pour les fuseaux négatifs.
 * On borne donc sur les instants réels de début et de fin de journée locale.
 */
function startOfLocalDay(day: string): string | null {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, 0, 0, 0, 0);
  return isNaN(date.getTime()) ? null : date.toISOString();
}

function endOfLocalDay(day: string): string | null {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, 23, 59, 59, 999);
  return isNaN(date.getTime()) ? null : date.toISOString();
}

function buildQuery(gymId: string, filters: AuditLogFilters, withCount: boolean) {
  const base = withCount
    ? supabase.from('audit_log_enriched').select('*', { count: 'exact' })
    : supabase.from('audit_log_enriched').select('*');

  let query = base
    .eq('gym_id', gymId)
    .order('created_at', { ascending: false });

  // Handle free-text search
  if (filters.search) {
    query = query.ilike('action', `%${filters.search}%`);
  }

  if (filters.fullNames?.length) {
    if (filters.fullNames.length === 1) {
      query = query.ilike('full_name', `%${filters.fullNames[0]}%`);
    } else {
      query = query.in('full_name', filters.fullNames);
    }
  }

  if (filters.startDate) {
    const from = startOfLocalDay(filters.startDate);
    if (from) query = query.gte('created_at', from);
  }
  if (filters.endDate) {
    const to = endOfLocalDay(filters.endDate);
    if (to) query = query.lte('created_at', to);
  }

  return query;
}

export function useAuditLogQuery(filters: AuditLogFilters, page: number, pageSize: number) {
  // gymId dans la clé : sans lui, le cache renvoyait le journal de la salle
  // précédente après un changement de salle.
  const gymId = useUserStore((state) => state.gymId);

  return useQuery({
    queryKey: ['audit-log', gymId, filters, page],
    enabled: !!gymId,
    queryFn: async () => {
      const query = buildQuery(gymId as string, filters, true)
        .range(page * pageSize, (page + 1) * pageSize - 1);

      const { data, count, error } = await query;
      if (error) throw error;
      return {
        rows: data ?? [],
        total: count ?? 0,
      };
    },
  });
}

/**
 * Lit toutes les lignes correspondant aux filtres, pour l'export CSV.
 * L'export ne contenait auparavant que la page affichée.
 */
export async function fetchAllAuditLogRows(
  gymId: string,
  filters: AuditLogFilters
): Promise<{ rows: AuditLogRow[]; truncated: boolean }> {
  const { data, error } = await buildQuery(gymId, filters, false)
    .range(0, AUDIT_LOG_EXPORT_LIMIT - 1);

  if (error) throw error;
  const rows = data ?? [];
  return { rows, truncated: rows.length >= AUDIT_LOG_EXPORT_LIMIT };
}
